// src/components/GoogleAnalytics.tsx

"use client"

import Script from "next/script"
import { useEffect, type ReactNode } from "react"
import {
  GA_MEASUREMENT_ID,
  shouldLoadGoogleAnalytics,
} from "~/lib/analytics/ga-config"

/**
 * Loads the GA4 network library (gtag.js) after the page is interactive.
 *
 * All consent-critical setup (the `gtag` stub, region-aware Consent Mode v2
 * defaults incl. GPC, the stored-choice replay, and `config` with
 * send_page_view:false) runs in a single synchronous inline <script> in the
 * document <head> (see the root layout + src/lib/analytics/consent-mode.ts), so
 * it is fully resolved before this library executes. This component only loads
 * the library.
 *
 * @returns The gtag.js loader, or null when analytics is disabled for this environment.
 */
export default function GoogleAnalytics(): ReactNode {
  const shouldLoad = shouldLoadGoogleAnalytics()

  const shouldLogGaStatus =
    process.env.NODE_ENV !== "production" ||
    process.env.NEXT_PUBLIC_GA_DEBUG === "true"

  useEffect(() => {
    if (!shouldLogGaStatus) return
    if (shouldLoad) {
      console.info("[Google Analytics] gtag.js loading (Consent Mode bootstrap in <head>)")
    } else {
      console.info("[Google Analytics] Disabled for this environment")
    }
  }, [shouldLoad, shouldLogGaStatus])

  if (!shouldLoad) {
    return null
  }

  return (
    <Script
      strategy="afterInteractive"
      src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
      onLoad={() => console.debug("[Analytics] GA loaded")}
      onError={(error) => console.error("[Analytics] GA script failed to load:", error)}
    />
  )
}
